import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ThemeProvider } from "@mui/material";
import { FEATURES, hasFeatureAccess } from "../helpers/featureAccess";
import { workareaTheme } from "../helpers/workareaTheme";
import { tipsRequest } from "../helpers/tipsWorkflow";
import LeagueChoice from "./LeagueChoice";
import "./ModelSeasonComparison.css";
export default function ModelSeasonComparison(props) {
  if (!hasFeatureAccess(FEATURES.STATISTICS_MODEL, props.user)) return null;
  return (
    <ThemeProvider theme={workareaTheme}>
      <Comparison {...props} />
    </ThemeProvider>
  );
}
function Comparison({ leagues = [], request = tipsRequest }) {
  const { i18n } = useTranslation(),
    en = i18n.language?.startsWith("en"),
    t = (de, eng) => (en ? eng : de);
  const [league, setLeague] = useState(""),
    [season, setSeason] = useState(""),
    [seasons, setSeasons] = useState([]),
    [data, setData] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [mode, setMode] = useState("common");
  const generation = useRef(0);
  useEffect(
    () => () => {
      generation.current++;
    },
    []
  );
  const check = (r) => {
    if (!r || r.success === false)
      throw Error(
        r?.code ||
          t("Daten konnten nicht geladen werden.", "Could not load data.")
      );
    return r;
  };
  async function loadSeason(lid, sid, g) {
    const r = check(
      await request("statistic-model", {
        mode: "comparison",
        league_id: Number(lid),
        season_id: Number(sid),
      })
    );
    if (g === generation.current) setData(r);
  }
  async function chooseLeague(id) {
    const g = ++generation.current;
    setLeague(id);
    setSeason("");
    setSeasons([]);
    setData(null);
    setError("");
    setBusy(true);
    try {
      const r = check(
        await request("statistic-model", {
          mode: "catalog",
          league_id: Number(id),
        })
      );
      if (g !== generation.current) return;
      setSeasons(r.seasons || []);
      const preferred =
        r.seasons?.find(
          (s) => s.name === "2025/2026" || s.name === "2025/26"
        ) ||
        r.seasons?.find((s) => s.name === "2025") ||
        r.seasons?.filter((s) => !s.is_current).at(-1);
      if (preferred) {
        setSeason(String(preferred.season_id));
        await loadSeason(id, preferred.season_id, g);
      }
    } catch (e) {
      if (g === generation.current) setError(e.message);
    } finally {
      if (g === generation.current) setBusy(false);
    }
  }
  async function chooseSeason(id) {
    const g = ++generation.current;
    setSeason(id);
    setData(null);
    setError("");
    setBusy(true);
    try {
      await loadSeason(league, id, g);
    } catch (e) {
      if (g === generation.current) setError(e.message);
    } finally {
      if (g === generation.current) setBusy(false);
    }
  }
  const fmt = (x, pct = false) =>
    Number.isFinite(x)
      ? (pct ? 100 * x : x).toLocaleString(en ? "en-GB" : "de-DE", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + (pct ? " %" : "")
      : "—";
  const sources = {
    result: t("Ergebnisbasiertes Modell", "Result-based model"),
    statistics: t("Statistikbasiertes Modell", "Statistics-based model"),
    market: t("Markt", "Market"),
    old_should: t(
      "Should · Standard-Conversion",
      "Should · standard conversion"
    ),
    adaptive_should: t(
      "Should · adaptive Conversion",
      "Should · adaptive conversion"
    ),
  };
  const provenanceLabels = {
    "result.probabilities.stored_fixture": t(
      "Ergebnis: gespeicherte Fixture-1X2",
      "Result: stored fixture 1X2"
    ),
    "result.probabilities.stored_expected_ledger": t(
      "Ergebnis: gespeicherte Saison-1X2",
      "Result: stored season 1X2"
    ),
    "result.probabilities.derived_season_parameters": t(
      "Ergebnis: 1X2 aus historischen Saisonparametern rekonstruiert",
      "Result: 1X2 reconstructed with historical season parameters"
    ),
    "result.probabilities.derived_frozen_result_parameters": t(
      "Ergebnis: 1X2 aus eingefrorener Modellbasis rekonstruiert",
      "Result: 1X2 reconstructed with frozen model basis"
    ),
    "result.expected_deviation.derived_historical_season_parameters": t(
      "Ergebnis: erwartete Torabweichung aus historischen Saisonparametern",
      "Result: expected goal deviation from historical season parameters"
    ),
    "result.expected_deviation.stored_score_matrix": t(
      "Ergebnis: erwartete Torabweichung aus gespeicherter Ergebnisverteilung",
      "Result: expected goal deviation from stored score distribution"
    ),
    "result.expected_deviation.derived_frozen_result_parameters": t(
      "Ergebnis: erwartete Torabweichung aus eingefrorener Modellbasis",
      "Result: expected goal deviation from frozen model basis"
    ),
    "statistics.probabilities.stored_statistics_prematch": t(
      "Statistik: gespeicherte 1X2",
      "Statistics: stored 1X2"
    ),
    "statistics.probabilities.derived_frozen_statistics_parameters": t(
      "Statistik: 1X2 aus eingefrorenen Parametern rekonstruiert",
      "Statistics: 1X2 reconstructed with frozen parameters"
    ),
    "statistics.expected_deviation.derived_frozen_statistics_parameters": t(
      "Statistik: erwartete Torabweichung aus eingefrorenen Parametern",
      "Statistics: expected goal deviation from frozen parameters"
    ),
    "market.probabilities.stored_market_probabilities": t(
      "Markt: gespeicherte 1X2",
      "Market: stored 1X2"
    ),
    "market.probabilities.derived_revised_odds_power": t(
      "Markt: 1X2 aus vorhandenen Quoten normalisiert",
      "Market: 1X2 normalized from existing odds"
    ),
    "market.expected_deviation.derived_stored_market_fit": t(
      "Markt: erwartete Torabweichung aus gespeicherter Rückrechnung",
      "Market: expected goal deviation from stored fit"
    ),
    "market.excluded_result_fallback.true": t(
      "Markt: ausgeschlossene Ergebnismodell-Fallbacks",
      "Market: excluded result-model fallbacks"
    ),
    "old_should.source.stored_should_ledger": t(
      "Alte Should Goals: gespeichert",
      "Old should goals: stored"
    ),
    "old_should.source.derived_existing_standard_conversion_0.0276": t(
      "Alte Should Goals: aus vollständigen Statistiken mit 0,0276 abgeleitet",
      "Old should goals: derived from complete statistics with 0.0276"
    ),
  };
  const definitions = {
    outcomes: {
      title: "1X2",
      hint: t(
        "Aufsummierte Wahrscheinlichkeiten ergeben erwartete Anzahlen, keine gerundeten Tipps.",
        "Summed probabilities give expected counts, not rounded predictions."
      ),
      labels: {
        home: t("Heimsiege", "Home wins"),
        draw: t("Remis", "Draws"),
        away: t("Auswärtssiege", "Away wins"),
      },
    },
    goals: {
      title: t("Tore", "Goals"),
      hint: t(
        "Heimvorteil nach betty: 2 × Heimtore / Gesamttore. 1,00 bedeutet ausgeglichen.",
        "betty home factor: 2 × home goals / total goals. 1.00 means balanced."
      ),
      labels: {
        home: t("Heimtore", "Home goals"),
        away: t("Auswärtstore", "Away goals"),
        total: t("Gesamttore", "Total goals"),
        average: t("Torschnitt", "Goals per match"),
        home_factor: t("Heimvorteil", "Home factor"),
      },
    },
    should: {
      title: t("Should-Goals-Vergleich", "Should-goals comparison"),
      hint: t(
        "Zusätzlicher Torvergleich, kein viertes Modell und keine daraus abgeleitete 1X2-Auswertung.",
        "Additional goals comparison, not a fourth model; no derived 1X2 analysis."
      ),
      labels: {
        home: t("Heimtore", "Home goals"),
        away: t("Auswärtstore", "Away goals"),
        total: t("Gesamttore", "Total goals"),
        average: t("Torschnitt", "Goals per match"),
      },
    },
    probabilities: {
      title: t("Wahrscheinlichkeitsvergleich", "Probability comparison"),
      hint: t(
        "AEP = Mittel von p(1)² + p(X)² + p(2)². AOP = mittlere Wahrscheinlichkeit des tatsächlichen Ausgangs.",
        "AEP = mean of p(1)² + p(X)² + p(2)². AOP = mean probability of the actual outcome."
      ),
      labels: {
        expected: "Average Expected Probability",
        occurred: "Average Occurred Probability",
      },
    },
    deviation: {
      title: t("Torabweichung", "Goal deviation"),
      hint: t(
        "Heim- und Auswärtsabweichung werden addiert, nicht halbiert. Erwartet: mit der Ergebnisverteilung gewichtet; eingetreten: |Tore − xG|.",
        "Home and away deviations are added, not halved. Expected: weighted by the score distribution; occurred: |goals − xG|."
      ),
      labels: {
        expected: t("Durchschnittlich erwartet", "Average expected"),
        occurred: t("Durchschnittlich eingetreten", "Average occurred"),
      },
    },
  };
  const reference = (a, key) =>
    !a
      ? t("Keine gemeinsame Datenbasis.", "No common data.")
      : key === "outcomes"
      ? `${t("Tatsächlich", "Actual")}: ${a.home_wins} / ${a.draws} / ${
          a.away_wins
        } (1 / X / 2)`
      : `${t(
          "Tatsächliche Tore Heim / Auswärts / Gesamt",
          "Actual goals home / away / total"
        )}: ${a.home_goals} / ${a.away_goals} / ${a.goals} · ${t(
          "Torschnitt",
          "Goals per match"
        )}: ${fmt(a.goal_average)}${
          key === "goals"
            ? ` · ${t("Heimvorteil", "Home factor")}: ${fmt(a.home_advantage)}`
            : ""
        }`;
  return (
    <section
      className="model-season-comparison"
      data-testid="model-season-comparison"
    >
      <h2>{t("Modell- und Marktvergleich", "Model and market comparison")}</h2>
      <p>
        {t(
          "Gespeicherte Werte und ausdrücklich gekennzeichnete Ableitungen. Historische Modellläufe sind Simulationen, kein Nachweis damaliger Live-Prognosen.",
          "Stored values and explicitly labelled derivations. Historical model runs are simulations, not evidence of forecasts made live at the time."
        )}
      </p>
      <div className="msc-controls">
        <LeagueChoice
          leagues={leagues}
          value={league}
          onChange={chooseLeague}
          label={t("Liga", "League")}
          placeholder={t("Liga auswählen", "Choose league")}
        />
        <label>
          {t("Saison", "Season")}
          <select
            aria-label={t("Saison", "Season")}
            value={season}
            disabled={!seasons.length || busy}
            onChange={(e) => chooseSeason(e.target.value)}
          >
            <option value="">—</option>
            {seasons.map((s) => (
              <option key={s.season_id} value={s.season_id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t("Datenbasis", "Sample")}
          <select
            aria-label={t("Datenbasis", "Sample")}
            value={mode}
            onChange={(e) => setMode(e.target.value)}
          >
            <option value="common">
              {t("Gemeinsame Spiele", "Common fixtures")}
            </option>
            <option value="available">
              {t("Alle verfügbaren Werte", "All available values")}
            </option>
          </select>
        </label>
      </div>
      {busy && (
        <p role="status">
          {t("Vergleich wird geladen …", "Loading comparison …")}
        </p>
      )}
      {error && <p role="alert">{error}</p>}
      {league && !busy && !error && !seasons.length && (
        <p>
          {t(
            "Keine gespeicherten Saisondaten für diese Liga.",
            "No stored seasons for this league."
          )}
        </p>
      )}
      {data && (
        <>
          <p>
            {data.season.name} · {data.finished_fixtures}{" "}
            {t(
              "abgeschlossene Spiele mit Ergebnis",
              "completed fixtures with score"
            )}
            .{" "}
            {mode === "common"
              ? t(
                  "Pro Bereich dieselben Spiele für alle dort verfügbaren Quellen. Fehlende Quellen bleiben leer.",
                  "Each section uses the same fixtures across its available sources. Missing sources stay empty."
                )
              : t(
                  "Eigene Datenabdeckung je Quelle; der tatsächliche Gegenwert darunter umfasst exakt dieselben Spiele.",
                  "Each source uses its own coverage; its actual reference covers exactly the same fixtures."
                )}
          </p>
          {Object.entries(definitions).map(([key, d]) => {
            const section = data.sections[key],
              view = section[mode];
            return (
              <section className="msc-section" aria-label={d.title} key={key}>
                <h3>{d.title}</h3>
                <p className="msc-note">{d.hint}</p>
                <p className="msc-reference">
                  {reference(view.actual, key)} · n = {view.count}
                </p>
                <div className="msc-cards">
                  {Object.entries(view.models).map(([source, m]) => (
                    <article
                      className={"msc-card msc-" + source}
                      key={source}
                      data-testid={key + "-" + source}
                    >
                      <h4>{sources[source]}</h4>
                      <p className="msc-count">
                        n = {m.count} · {t("verfügbar", "available")}:{" "}
                        {section.coverage[source]} / {data.finished_fixtures}
                      </p>
                      {m.values ? (
                        <dl>
                          {Object.entries(d.labels).map(([field, label]) => (
                            <div key={field}>
                              <dt>
                                {label}
                                {key === "deviation" && field === "expected"
                                  ? ` (n = ${m.values.expected_count})`
                                  : ""}
                              </dt>
                              <dd>
                                {fmt(m.values[field], key === "probabilities")}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      ) : (
                        <p>
                          {t(
                            "Keine Vergleichsdaten vorhanden.",
                            "No comparison data available."
                          )}
                        </p>
                      )}
                      {mode === "available" && m.actual && (
                        <p className="msc-matched">
                          {reference(m.actual, key)}
                        </p>
                      )}
                    </article>
                  ))}
                </div>
              </section>
            );
          })}
          <details className="msc-method">
            <summary>
              {t("Datenquellen und Ableitungen", "Sources and derivations")}
            </summary>
            <p>
              {t(
                "Ergebnis: gespeicherte Fixture-Werte, ergänzend Expected-Ledger. Fehlende 1X2 und erwartete Torabweichung werden nur mit vorhandenen Parametern derselben Saison über die bestehende betty-Engine rekonstruiert. Statistik: eingefrorene Pre-Match-Werte und individuelle Conversion. Markt: gespeicherte unabhängige Rückrechnung; 1X2 aus vorhandenen Wahrscheinlichkeiten oder der bestehenden Revised-Odds-Normalisierung. xG allein bestimmt keine erwartete Torabweichung. Marktwerte mit betty-Fallback werden nicht als unabhängige Torprognose gezählt.",
                "Result: stored fixture values, supplemented by the expected ledger. Missing 1X2 and expected goal deviation are reconstructed through the existing betty engine only when the same season’s parameters exist. Statistics: frozen pre-match values and individual conversion. Market: stored independent fit; 1X2 from stored probabilities or existing revised-odds normalization. xG alone does not determine expected goal deviation. Market betty fallbacks are excluded as independent goal predictions."
              )}
            </p>
            <ul>
              {Object.entries(data.provenance)
                .filter(([k]) => provenanceLabels[k])
                .map(([k, n]) => (
                  <li key={k}>
                    {provenanceLabels[k]}: {n}
                  </li>
                ))}
            </ul>
          </details>
        </>
      )}
    </section>
  );
}
